import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, ArrowRight, AlertCircle, Calendar, Clock } from "lucide-react";

const sb = supabase as any;

export const SpacedReviewWidget = () => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["sr-widget-stats", user?.id],
    enabled: !!user?.id,
    refetchInterval: 60000,
    queryFn: async () => {
      const now = new Date();
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);
      const in7days = new Date(now.getTime() + 7 * 86400000);

      const [overdueRes, todayRes, upcomingRes, totalRes] = await Promise.all([
        sb.from("spaced_repetition_queue").select("id", { count: "exact", head: true })
          .eq("user_id", user!.id).lt("next_review_at", now.toISOString()),
        sb.from("spaced_repetition_queue").select("id", { count: "exact", head: true })
          .eq("user_id", user!.id)
          .gte("next_review_at", now.toISOString())
          .lte("next_review_at", endOfToday.toISOString()),
        sb.from("spaced_repetition_queue").select("id", { count: "exact", head: true })
          .eq("user_id", user!.id)
          .gt("next_review_at", endOfToday.toISOString())
          .lte("next_review_at", in7days.toISOString()),
        sb.from("spaced_repetition_queue").select("id", { count: "exact", head: true })
          .eq("user_id", user!.id),
      ]);

      return {
        overdue: overdueRes.count ?? 0,
        dueToday: todayRes.count ?? 0,
        upcoming: upcomingRes.count ?? 0,
        total: totalRes.count ?? 0,
      };
    },
  });

  if (isLoading || !data || data.total === 0) return null;

  const dueNow = data.overdue + data.dueToday;

  return (
    <Card className="medical-card border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Spaced Repetition Review
          </div>
          {dueNow > 0 && (
            <Badge variant="default" className="bg-gradient-medical">
              {dueNow} ready
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-center">
            <AlertCircle className="h-4 w-4 mx-auto mb-1 text-destructive" />
            <div className="text-2xl font-bold text-destructive">{data.overdue}</div>
            <div className="text-xs text-muted-foreground">Overdue</div>
          </div>
          <div className="rounded-lg border border-orange-300/50 bg-orange-50 dark:bg-orange-950/20 p-3 text-center">
            <Clock className="h-4 w-4 mx-auto mb-1 text-orange-600" />
            <div className="text-2xl font-bold text-orange-600">{data.dueToday}</div>
            <div className="text-xs text-muted-foreground">Due today</div>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
            <Calendar className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <div className="text-2xl font-bold text-foreground">{data.upcoming}</div>
            <div className="text-xs text-muted-foreground">Next 7 days</div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {dueNow > 0
              ? `${dueNow} question${dueNow !== 1 ? "s" : ""} ready for review now.`
              : `All caught up! ${data.upcoming} question${data.upcoming !== 1 ? "s" : ""} coming up this week.`}
          </p>
          <Button size="sm" className="bg-gradient-medical shrink-0" disabled={dueNow === 0} asChild={dueNow > 0}>
            {dueNow > 0 ? (
              <Link to="/practice?mode=review">
                Start Review <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            ) : (
              <span>Up to date</span>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
